import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';

import type { TileBigNumber } from '@/features/dashboard/types';

import type { DashboardEditState } from '../dashboardEdit.types';
import { getCurrentTabDraft } from '../lib/dashboardEditValidation';
import { insertAtIndex, reorderArray } from '../lib/reorder';

type State = DashboardEditState;

const addTile: CaseReducer<
  State,
  PayloadAction<{ tabId: string; tile: TileBigNumber; insertAtIndex?: number }>
> = (state, action) => {
  const { tabId, tile, insertAtIndex: insertIdx } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;

  draft.tiles = insertAtIndex(draft.tiles, structuredClone(tile), insertIdx);
  state.isDirty = true;
};

const removeTile: CaseReducer<State, PayloadAction<{ tabId: string; tileId: string }>> = (
  state,
  action
) => {
  const draft = state.tabDrafts[action.payload.tabId];
  if (!draft) return;

  draft.tiles = draft.tiles.filter(t => t.id !== action.payload.tileId);
  state.isDirty = true;
};

const updateTile: CaseReducer<
  State,
  PayloadAction<{ tabId: string; tileId: string; tile: TileBigNumber }>
> = (state, action) => {
  const { tabId, tileId, tile } = action.payload;
  const draft = state.tabDrafts[tabId];
  if (!draft) return;

  const index = draft.tiles.findIndex(t => t.id === tileId);
  if (index !== -1) {
    draft.tiles[index] = structuredClone(tile);
    state.isDirty = true;
  }
};

const reorderTiles: CaseReducer<
  State,
  PayloadAction<{ tabId: string; fromIndex: number; toIndex: number }>
> = (state, action) => {
  const draft = state.tabDrafts[action.payload.tabId];
  if (!draft) return;

  draft.tiles = reorderArray(draft.tiles, action.payload.fromIndex, action.payload.toIndex);
  state.isDirty = true;
};

const updateCurrentTabTiles: CaseReducer<State, PayloadAction<{ tiles: TileBigNumber[] }>> = (
  state,
  action
) => {
  const draft = getCurrentTabDraft(state);
  if (!draft) return;

  draft.tiles = action.payload.tiles.map(tile => structuredClone(tile));
  state.isDirty = true;
};

const setInsertPosition: CaseReducer<
  State,
  PayloadAction<{ index: number | null; afterId: string | null } | null>
> = (state, action) => {
  state.insertPosition = action.payload;
};

export const tileReducers = {
  addTile,
  removeTile,
  updateTile,
  reorderTiles,
  updateCurrentTabTiles,
  setInsertPosition
};
